import { KycData, SubmitProps } from '../KycForm';
import OrgsService from '../../services/orgs';
import { ReplaceKycProps } from '.';
import logger, { logVgsFormSubmitResponse } from '../../util/logger';
import { createShortLivedToken } from '../../services/vault/token';
import { replaceAccountRepresentative } from '../../services/vault/stripeConnectAccount';
import { createKycRequestBodyFormatter } from '../KycForm/createKycRequestBodyFormatter';
import { financialAccountUserAssociate } from '../../services/orgs/financialAccountUserAssociate';

const submit =
  ({
    stripeEnv,
    acctId,
    currentUserId,
    isRepresentative,
    isExecutive,
    isDirector,
    isOwner,
    askIsOwner,
    onSuccess,
  }: ReplaceKycProps) =>
  async ({
    form,
    kycAddressRegion,
    kycPercentOwnership,
    kycTitle,
    kycOwner,
    provider,
  }: SubmitProps): Promise<void> => {
    const token = await createShortLivedToken();

    return new Promise((resolve, reject) => {
      form.submit(
        '/graphql',
        {
          data: createKycRequestBodyFormatter({
            kycAddressRegion,
            kycDirector: isDirector,
            kycExecutive: isExecutive,
            // if is owner checkbox is rendered to user and user checks it
            // or the consuming app has set isOwner = true
            // kyc is an owner
            kycOwner:
              askIsOwner && typeof kycOwner === 'boolean' ? kycOwner : isOwner,
            kycPercentOwnership,
            kycRepresentative: isRepresentative,
            kycTitle,
          }),
          headers: {
            Authorization: `Bearer ${token}`,
          },
          withCredentials: true,
        },
        async (responseStatus, { data, errors }) => {
          logVgsFormSubmitResponse({
            formName: 'ReplaceKyc',
            responseStatus,
            data,
            errors,
          });
          if (responseStatus !== 200) {
            throw new Error(`Received ${responseStatus} status.`);
          }

          if (typeof data === 'string') {
            throw new Error(data);
          }

          const financialAccount = await OrgsService.financialAcctId(acctId);
          logger('ReplaceKyc financialAccount', false, financialAccount);
          if (!financialAccount?.finAcctId) {
            throw new Error('financial account missing finAcctId');
          }

          const representative = await OrgsService.getAccountRepresentative(
            financialAccount.finAcctId,
          );
          const usersThatShareKyc =
            await OrgsService.getFinancialAcctUsersByKycId(
              representative.kycId,
            );
          logger('ReplaceKyc usersThatShareKyc', false, usersThatShareKyc);
          const removePromises: Promise<KycData>[] = usersThatShareKyc
            .filter(
              (u) =>
                u.userId !== undefined &&
                u.finAcctId !== undefined &&
                u.acctId !== undefined,
            )
            .map((user) =>
              (async (): Promise<KycData> => {
                logger(
                  `ReplaceKyc deleting financial account user association between ${user.finAcctId} and ${user.userId}`,
                );
                // user.finAcctId and user.userId confirmed to not be undefined in filter
                await OrgsService.deleteFinancialAcctUser(
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  user.finAcctId!,
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  user.userId!,
                );

                let personId: string | undefined;
                if (provider === 'stripe') {
                  logger(
                    'ReplaceKyc replacing account representative',
                    false,
                    user.acctId,
                    data.vaultKycCreate,
                    stripeEnv,
                  );
                  personId = await replaceAccountRepresentative(
                    // user.acctId confirmed to not be undefined in filter
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    user.acctId!,
                    data.vaultKycCreate,
                    stripeEnv,
                  );
                } else if (provider === 'unit') {
                  throw new Error('Unit not yet implemented');
                }
                logger('ReplaceKyc new personId', false, personId);

                if (personId === undefined) {
                  throw new Error('Could not create stripe person');
                }

                logger(
                  `ReplaceKyc About to associate user ${currentUserId} to ${user.finAcctId}`,
                );
                await financialAccountUserAssociate({
                  // user.finAcctId confirmed to not be undefined in filter
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  finAcctId: user.finAcctId!,
                  userId: currentUserId,
                  isBeneficialOwner: false,
                  kycId: data.vaultKycCreate,
                  personId,
                  isRepresentative: true,
                });

                return {
                  kycId: data.vaultKycCreate,
                  personId,
                  provider,
                  userId: currentUserId,
                };
              })(),
            );

          const successData = await Promise.all(removePromises);
          if (onSuccess !== undefined) {
            logger(`ReplaceKyc successData ${JSON.stringify(successData)}`);
            onSuccess(successData);
          }
          resolve();
        },
        (errors) => {
          reject(errors);
        },
      );
    });
  };

export default submit;
